import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, RefreshCw } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { PageTitleWithHelp } from "@/components/ui/title-help";

import { fetchBenchMachines } from "./benchesApi";
import { benchDisplayLabel } from "./benchesLabel";

const PAGE_HELP =
  "Registered SDTestApp bench PCs heartbeat to Hub. Open a bench to see SDTestApp connection status on this workstation or observe remotely via Hub relay.";

export function BenchesPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const legacyMachineId = String(searchParams.get("station") ?? searchParams.get("machine") ?? "").trim();
    if (legacyMachineId) {
      navigate(`/manufacturing/benches/${encodeURIComponent(legacyMachineId)}`, { replace: true });
    }
  }, [navigate, searchParams]);

  const benchesQuery = useQuery({
    queryKey: ["bench-machines"],
    queryFn: fetchBenchMachines,
    refetchInterval: 30_000,
  });

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 p-4">
      <PageTitleWithHelp hoverHint={PAGE_HELP}>SDTestApp benches</PageTitleWithHelp>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">Registered bench PCs</h2>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void benchesQuery.refetch()}
            disabled={benchesQuery.isFetching}
          >
            {benchesQuery.isFetching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh
          </Button>
        </CardHeader>
        <CardBody className="space-y-3">
          {benchesQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading benches…</p>
          ) : (benchesQuery.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No benches registered yet. Set Override PC name in SDTestApp Settings (or leave blank to use the Windows PC name) and ensure the engine can reach Hub.
            </p>
          ) : (
            <ul className="divide-y rounded-md border">
              {(benchesQuery.data ?? []).map((bench) => (
                <li key={bench.machineId} className="flex flex-wrap items-center justify-between gap-3 px-3 py-2">
                  <div>
                    <div className="font-medium">{benchDisplayLabel(bench)}</div>
                    <div className="text-xs text-muted-foreground">
                      Engine {bench.engineVersion ?? "unknown"} · last seen {new Date(bench.lastSeenAt).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge tone={bench.online ? "success" : "muted"}>{bench.online ? "Online" : "Offline"}</Badge>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => navigate(`/manufacturing/benches/${encodeURIComponent(bench.machineId)}`)}
                    >
                      Open
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
